import React from "react";
import PropTypes from "kf-proptypes/widgets";

import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import { RATING_SIZES } from "./constants.js";
import { ReadonlyRating } from "./readonly.js";
import { EditableRating } from "./editable.js";

export function Rating({
  id,
  required,
  value,
  readOnly,
  handleChange,
  size = RATING_SIZES.MICRO,
  isError,
  className,
  disabled,
  hint,
  errors,
  label,
  labelLeftIcon,
  labelRightIcon,
  allowHalf = false
}) {
  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          disabled={disabled}
          targetId={id}
          required={required}
          label={label}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readOnly ? (
        <ReadonlyRating
          value={value}
          size={size}
          isError={isError}
          className={className}
          allowHalf={allowHalf}
        />
      ) : (
        <EditableRating
          value={value}
          handleChange={handleChange}
          size={size}
          isError={isError}
          className={className}
          disabled={disabled}
          allowHalf={allowHalf}
        />
      )}
      {hint && <FieldHint disabled={disabled} hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors disabled={disabled} errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

Rating.propTypes = {
  /**
   * The unique identifier for the Rating field.
   */
  id: PropTypes.string,
  /**
   * Determines if the Rating field is required.
   */
  required: PropTypes.bool,
  /**
   * The rating value to be displayed
   */
  value: PropTypes.number.isRequired,
  /**
   * The label text for the Rating field.
   */
  label: PropTypes.string,
  /**
   * Provides a hint or additional information to assist users
   */
  hint: PropTypes.string,
  /**
   * Callback function triggered when the rating value changes.
   */
  handleChange: PropTypes.func,
  /**
   * Additional className for Rating Component
   */
  className: PropTypes.string,
  /**
   * The size of the stars
   */
  size: PropTypes.oneOf(Object.values(RATING_SIZES)),
  /**
   * Determines if the rating is read-only or interactive.
   */
  readOnly: PropTypes.bool,
  /**
   * Determines if the rating is disabeld or active.
   */
  disabled: PropTypes.bool,
  /**
   * Determines if the Rating field has an error.
   */
  isError: PropTypes.bool,
  /**
   * An array of error messages associated with the input field
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node,
  /** Determines if the rating is allowing half star incrementation or not. */
  allowHalf: PropTypes.bool
};

export { RATING_SIZES };
